import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Award, XCircle, Clock, FileDown, Loader2, GraduationCap, RotateCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DECISION_UI = {
  certifie: { label: "CERTIFIÉ(E)", icon: Award, color: "#10b981", bg: "rgba(16,185,129,0.12)", msg: "Félicitations ! Le jury vous a déclaré(e) certifié(e)." },
  ajourne: { label: "AJOURNÉ(E)", icon: RotateCcw, color: "#f59e0b", bg: "rgba(245,158,11,0.12)", msg: "Vous n'avez pas validé toutes les épreuves — un rattrapage est possible." },
  absent: { label: "ABSENT(E)", icon: XCircle, color: "#94a3b8", bg: "rgba(148,163,184,0.12)", msg: "Vous étiez absent(e) à cette session d'examen." },
};

export default function ResultatCandidatPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/certif/portal/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Lien invalide ou expiré"));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-[#0b0811] flex items-center justify-center p-4">
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-8 text-center max-w-md" data-testid="portal-error">
        <XCircle className="w-10 h-10 text-rose-400 mx-auto mb-3" />
        <p className="text-white font-bold">{error}</p>
        <p className="text-xs text-slate-500 mt-2">Vérifiez le lien reçu par email ou contactez votre organisme certificateur.</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-[#0b0811] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />
    </div>
  );
  if (data.pending) return (
    <div className="min-h-screen bg-[#0b0811] flex items-center justify-center p-4">
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-8 text-center max-w-md" data-testid="portal-pending">
        <Clock className="w-10 h-10 text-[#f7c935] mx-auto mb-3" />
        <p className="text-white font-bold">Résultats en cours de délibération</p>
        <p className="text-xs text-slate-500 mt-2">Revenez consulter cette page après la clôture de la session.</p>
      </div>
    </div>
  );

  const ui = DECISION_UI[data.decision] || DECISION_UI.absent;
  const Icon = ui.icon;
  const sc = data.scores || {};

  return (
    <div className="min-h-screen bg-[#0b0811] flex items-center justify-center p-4" data-testid="portal-result-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-5">
          <GraduationCap className="w-8 h-8 text-[#f7c935] mx-auto mb-2" />
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">{data.certification?.organisme || "Résultat d'examen"}</p>
          <h1 className="text-lg font-bold text-white mt-1">
            {data.certification?.code ? `${data.certification.code} — ` : ""}{data.certification?.titre}
          </h1>
          <p className="text-xs text-slate-500">Session du {data.session?.date_examen}{data.session?.lieu ? ` · ${data.session.lieu}` : ""}</p>
        </div>

        <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 space-y-5">
          <div className="text-center">
            <p className="text-sm text-slate-400 mb-3">{data.candidat?.prenom} <span className="uppercase">{data.candidat?.nom}</span></p>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black text-lg"
              style={{ background: ui.bg, color: ui.color }} data-testid="portal-decision-badge">
              <Icon className="w-5 h-5" /> {ui.label}
            </div>
            <p className="text-xs text-slate-500 mt-3">{ui.msg}</p>
          </div>

          {data.decision !== "absent" && (
            <div className="grid grid-cols-2 gap-3">
              <ScoreCard label="Épreuve 1 — QCM" value={sc.qcm} max={sc.qcm_bareme} seuil={sc.qcm_seuil}
                ok={(data.epreuves_validees || []).includes("qcm")} testId="portal-score-qcm" />
              <ScoreCard label="Épreuve 2 — Pratique" value={sc.pratique} max={sc.pratique_max} seuil={sc.pratique_seuil}
                ok={(data.epreuves_validees || []).includes("pratique")} testId="portal-score-pratique" />
            </div>
          )}

          {data.diplome_disponible && (
            <a href={`${API}/api/certif/portal/${token}/diplome.pdf`}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825] transition-colors"
              data-testid="portal-download-diploma-btn">
              <FileDown className="w-4 h-4" /> Télécharger mon diplôme (PDF)
            </a>
          )}

          {data.rattrapage && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="portal-rattrapage-info">
              <p className="text-sm font-bold text-amber-300 flex items-center gap-1.5"><RotateCcw className="w-4 h-4" /> Rattrapage possible</p>
              <p className="text-xs text-slate-400 mt-1.5">
                Épreuve(s) à repasser : <strong className="text-white">{(data.rattrapage.epreuves || []).join(" + ") || "—"}</strong>.
                Vos épreuves validées sont conservées. Date limite : <strong className="text-white">{data.rattrapage.deadline}</strong>.
              </p>
              <p className="text-[11px] text-slate-500 mt-1.5">Contactez votre organisme pour planifier votre session de rattrapage.</p>
            </div>
          )}
        </div>
        <p className="text-center text-[10px] text-slate-600 mt-4">Document consultatif — seul le procès-verbal de délibération fait foi.</p>
      </div>
    </div>
  );
}

function ScoreCard({ label, value, max, seuil, ok, testId }) {
  const pct = value !== null && value !== undefined && max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl p-3.5" data-testid={testId}>
      <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">{label}</p>
      <p className="text-xl font-black text-white tabular-nums">
        {value ?? "—"}<span className="text-xs text-slate-500 font-normal"> / {max}</span>
      </p>
      <div className="h-1.5 rounded-full bg-white/10 mt-2 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ok ? "#10b981" : "#f59e0b" }} />
      </div>
      <p className="text-[10px] mt-1.5" style={{ color: ok ? "#10b981" : "#f59e0b" }}>
        {ok ? "✓ Épreuve validée" : `Seuil requis : ${seuil}`}
      </p>
    </div>
  );
}
